import { useEffect, useState } from "react";
import { Star, ExternalLink, Loader2, MessageSquare } from "lucide-react";
import api from "../../lib/api";

// Renders the live Google reviews summary + latest 5 reviews returned by
// /api/reviews/google (backend fetches from Google Places API New with 6h
// MongoDB cache, so this is cheap to hit on every page render).
//
// Variants:
//   variant="strip"    — compact horizontal trust bar (rating + count only)
//   variant="grid"     — full section with 5 review cards (default)

const Stars = ({ value = 5, size = 12 }) => (
  <span className="inline-flex items-center gap-0.5 text-[hsl(var(--accent))]" aria-label={`${value} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={size} className={i <= Math.round(value) ? "fill-[hsl(var(--accent))]" : "opacity-30"} strokeWidth={1.5} />
    ))}
  </span>
);

const GOOGLE_REVIEW_URL = "https://search.google.com/local/writereview?placeid=ChIJqU-kb_YxjjkRr_8zU67l7Bg";

const GoogleReviewsLive = ({ variant = "grid", limit = 5 }) => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api.get("/reviews/google").then((r) => setData(r.data)).catch(() => setFailed(true));
  }, []);

  if (failed) return null;
  if (!data) {
    return (
      <div className="text-xs text-muted-foreground inline-flex items-center gap-2" data-testid="google-reviews-loading">
        <Loader2 size={12} className="animate-spin" /> Loading Google reviews…
      </div>
    );
  }

  const rating = data.rating ?? 4.8;
  const count = data.review_count ?? 900;
  const reviews = (data.reviews || []).slice(0, limit);

  if (variant === "strip") {
    return (
      <a
        href={data.google_maps_uri || "https://g.page/shantanu-netralaya-varanasi"}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-border bg-background hover:border-[hsl(var(--accent))]/50 transition-colors"
        data-testid="google-reviews-strip"
      >
        <Stars value={rating} size={13} />
        <span className="text-sm font-medium">{Number(rating).toFixed(1)}</span>
        <span className="text-xs text-muted-foreground">· {count.toLocaleString("en-IN")} Google reviews</span>
        <ExternalLink size={11} className="text-muted-foreground" />
      </a>
    );
  }

  return (
    <section className="container-page py-14" data-testid="google-reviews-live">
      <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
        <div>
          <div className="overline mb-2 inline-flex items-center gap-2">
            <MessageSquare size={12} /> Live Google reviews
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl leading-tight">
            {Number(rating).toFixed(1)}★ from {count.toLocaleString("en-IN")} Google reviews
          </h2>
          <div className="mt-2 text-xs text-muted-foreground">
            Auto-updated from our Google Business Profile · Refreshed every 6 hours.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <a
            href={data.google_maps_uri || "https://g.page/shantanu-netralaya-varanasi"}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline text-xs inline-flex items-center gap-2"
            data-testid="google-reviews-read-all"
          >
            Read all on Google <ExternalLink size={12} />
          </a>
          <a
            href={GOOGLE_REVIEW_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-xs inline-flex items-center gap-2"
            data-testid="google-reviews-leave"
          >
            Leave a review
          </a>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {reviews.map((r, idx) => (
          <article
            key={`${r.author}-${idx}`}
            className="p-6 rounded-2xl border border-border bg-background flex flex-col"
            data-testid="google-review-card"
          >
            <div className="flex items-center gap-3">
              {r.author_photo_url ? (
                <img
                  src={r.author_photo_url}
                  alt={r.author || "Reviewer"}
                  className="w-10 h-10 rounded-full object-cover"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-sm font-medium text-[hsl(var(--primary))]">
                  {(r.author || "?").slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.author}</div>
                <div className="text-[11px] text-muted-foreground">{r.relative_time}</div>
              </div>
            </div>
            <div className="mt-3">
              <Stars value={r.rating || 5} size={12} />
            </div>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed line-clamp-6">
              {r.text || "(No written review)"}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
};

export default GoogleReviewsLive;
